import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import * as UserActions from '@store/user/user.actions';
import { selectLoginErrors } from '@store/user/user.selector';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs/operators';
import { LoginErrors } from '@store/user/user.model';
import { RibbonService } from '@shared/services/ribbon.service';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

@UntilDestroy()
@Component({
  selector: 'rlst-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent implements OnInit {
  loginForm: UntypedFormGroup;
  usernameControl: UntypedFormControl;
  passwordControl: UntypedFormControl;
  mlsGroupControl: UntypedFormControl;

  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly store$: Store<AppState>,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly ribbonService: RibbonService,
    private readonly dialog: MatDialog
  ) {
  }

  ngOnInit() {
    this.dialog.closeAll();

    this.createForm();
    this.store$
      .pipe(
        select(selectLoginErrors),
        filter((loginErrors) => !!loginErrors),
        untilDestroyed(this)
      )
      .subscribe((loginErrors) => {
        this.setLoginErrors(loginErrors);
        this.changeDetector.markForCheck();
      });
  }

  logIn(): void {
    if (this.loginForm.valid) {
      sessionStorage.removeItem('hmrState');

      this.store$.dispatch(new UserActions.Login(this.loginForm.value));
    }
  }

  private createForm(): void {
    this.loginForm = this.fb.group({
      username: [null, [Validators.required]],
      password: [null, [Validators.required]],
      mlsGroup: [null, [Validators.required]]
    });

    this.usernameControl = this.loginForm.get('username') as UntypedFormControl;
    this.passwordControl = this.loginForm.get('password') as UntypedFormControl;
    this.mlsGroupControl = this.loginForm.get('mlsGroup') as UntypedFormControl;
  }

  private setLoginErrors(loginErrors: LoginErrors): void {
    if (loginErrors.hasOwnProperty('loginError')) {
      this.ribbonService.openRibbon(
        RIBBON_TYPE.ERROR,
        'Something went wrong. Please try again later or contact customer support',
        {panelClass: ['ribbon', 'skip-header']}
      );
      return;
    }
    for (const controlName of Object.keys(loginErrors)) {
      const formControl = this.loginForm.get(controlName);
      const errorMessage = loginErrors[controlName];
      if (formControl && errorMessage) {
        formControl.setErrors({
          'loginError': errorMessage
        });
      }
    }
  }
}
